import { FooterNewsletter } from '@/components/footer-newsletter'
import { OorvaLogo } from '@/components/ui/oorva-logo'
import { ArrowUpRight, Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter, Youtube } from 'lucide-react'
import Link from 'next/link'

const exploreLinks = [
  { label: 'Experiences', href: '/experiences' },
  { label: 'Weekend in Thanjavur', href: '/weekend-trips' },
  { label: 'Destinations', href: '/destinations' },
  { label: 'Explore Thanjavur', href: '/thanjavur' },
  { label: 'Oorva marketplace', href: 'https://app.oorva.in', external: true },
]

const partnerLinks = [
  { label: 'Become a host', href: '/host' },
  { label: 'Collaborations', href: '/collaborations' },
  { label: 'Weekend trips', href: '/weekend-trips' },
  { label: 'RIIP for institutions', href: '/riip' },
  { label: 'Oorva merchandise', href: '/merch' },
]

export function Footer() {
  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-primary text-primary-foreground">
      <div className="site-shell relative py-9 sm:py-11 lg:py-12">
        <div className="grid gap-7 border-b border-white/12 pb-8 sm:pb-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <p className="flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.2em] text-brand-highlight">
              <span className="h-px w-8 bg-current" aria-hidden="true" />
              Travel deeper with Oorva
            </p>
            <h2 className="mt-4 max-w-3xl text-[clamp(2.5rem,5vw,4.75rem)] font-semibold leading-[0.94] tracking-[-0.055em]">
              Go beyond the itinerary.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-white/62 sm:text-base">
              Discover places through the people who know them best, with thoughtful
              experiences rooted in local life.
            </p>
          </div>
          <Link
            href="https://app.oorva.in"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex min-h-14 w-full items-center justify-between gap-6 rounded-full bg-brand py-2 pl-6 pr-2 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-hover lg:w-auto"
          >
            Explore experiences
            <span className="grid size-10 place-items-center rounded-full bg-white text-primary transition-transform duration-300 group-hover:rotate-45">
              <ArrowUpRight className="size-4" />
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-8 py-8 sm:py-10 lg:grid-cols-12 lg:py-11">
          <div className="col-span-2 flex flex-col items-start lg:col-span-4">
            <Link href="/" className="-ml-2 inline-block" aria-label="Oorva home">
              <OorvaLogo showText size="lg" />
            </Link>
            <p className="mt-3 max-w-sm text-sm leading-7 text-white/58">
              Thoughtfully curated local experiences for travellers who want to meet India
              through its people, places and living traditions.
            </p>
            {/* Visible NAP — keeps footer contact consistent with Organization JSON-LD for local SEO. */}
            <address className="mt-4 flex items-start gap-2 text-sm not-italic leading-6 text-white/62">
              <MapPin className="mt-1 size-4 shrink-0 text-brand-highlight" aria-hidden="true" />
              <span>Oorva Private Limited, Chennai, Tamil Nadu, India</span>
            </address>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                href="https://instagram.com/oorva.in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oorva on Instagram"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-xs font-semibold text-white/68 transition hover:border-white/28 hover:text-white"
              >
                <Instagram className="size-3.5" />
                Instagram
              </Link>
              <Link
                href="https://www.linkedin.com/company/oorva-hosted-by-locals"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oorva on LinkedIn"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-xs font-semibold text-white/68 transition hover:border-white/28 hover:text-white"
              >
                <Linkedin className="size-3.5" />
                LinkedIn
              </Link>
              <Link
                href="https://www.youtube.com/@oorva_in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oorva on YouTube"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-xs font-semibold text-white/68 transition hover:border-white/28 hover:text-white"
              >
                <Youtube className="size-3.5" />
                YouTube
              </Link>
              <Link
                href="https://x.com/oorva_in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oorva on X"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-xs font-semibold text-white/68 transition hover:border-white/28 hover:text-white"
              >
                <Twitter className="size-3.5" />
                X
              </Link>
              <Link
                href="https://www.facebook.com/oorva.in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Oorva on Facebook"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/12 px-4 text-xs font-semibold text-white/68 transition hover:border-white/28 hover:text-white"
              >
                <Facebook className="size-3.5" />
                Facebook
              </Link>
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterColumn title="Explore" links={exploreLinks} />
          </div>
          <div className="lg:col-span-2">
            <FooterColumn title="Partner with us" links={partnerLinks} />
          </div>

          <div className="col-span-2 rounded-3xl border border-white/12 bg-white/[0.055] p-5 sm:p-6 lg:col-span-4">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-brand-highlight">
              Notes from the road
            </p>
            <h3 className="mt-3 text-xl font-semibold tracking-[-0.025em]">Stay inspired, occasionally.</h3>
            <p className="mt-2 text-sm leading-6 text-white/58">
              New experiences, local stories and considered travel ideas.
            </p>
            <FooterNewsletter />
            <div className="mt-4 flex flex-col gap-1 border-t border-white/10 pt-3 text-xs text-white/62 sm:flex-row sm:flex-wrap sm:gap-x-5">
              <Link href="mailto:hello@oorva.in" className="flex min-h-8 items-center gap-2 transition hover:text-white">
                <Mail className="size-3.5 text-brand-highlight" />
                hello@oorva.in
              </Link>
              <Link href="tel:+918754035228" className="flex min-h-8 items-center gap-2 transition hover:text-white">
                <Phone className="size-3.5 text-brand-highlight" />
                +91 87540 35228
              </Link>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5 border-t border-white/10 pt-6 text-xs text-white/42 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Oorva Private Limited. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <Link href="/docs/guest-policy" className="transition hover:text-white">Guest policy</Link>
            <Link href="/docs/terms-and-conditions" className="transition hover:text-white">Terms &amp; conditions</Link>
            <Link href="/docs/privacy-policy" className="transition hover:text-white">Privacy policy</Link>
            <span>Made with care in India.</span>
          </div>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  title,
  links,
}: {
  title: string
  links: Array<{ label: string; href: string; external?: boolean }>
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-white/42">{title}</h3>
      <ul className="mt-4 flex flex-col gap-1">
        {links.map((link) => (
          <li key={link.label}>
            <Link
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              className="group flex min-h-10 items-center justify-between gap-3 text-sm text-white/62 transition hover:text-white"
            >
              <span>{link.label}</span>
              {link.external && <ArrowUpRight className="size-3.5 text-white/28 transition group-hover:text-brand-highlight" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
